/**
 * Visit scheduling service — shared data layer for On-Site & CQC 2-day visits.
 *
 * Data model
 * ----------
 * - `visitWindows`        : admin-defined open 2-day windows.
 *     { id, type:'onsite'|'cqc', day1:'YYYY-MM-DD', day2:'YYYY-MM-DD', capacity:Number, createdAt, updatedAt }
 * - `onSiteRequests` / `cqcVisitRequests` : one doc per client (id = user uid).
 *     { userId, userEmail, windowId, day1, day2, status, createdAt, updatedAt, manualEntry?, companyName?, requestedByName? }
 *     status: 'requested' | 'scheduled' | 'completed' | 'denied'
 * - `visitConfig`         : docs `onsite` / `cqc` hold the editable itinerary.
 *     { itinerary:[ { day, title, blocks:[ { time, activity } ] } ], updatedAt }
 *
 * A window holds up to `capacity` companies (default 2). Two companies may share
 * the same two days. Availability = future windows whose active (requested+scheduled)
 * count is below capacity.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { isActiveClientAccount } from '../utils/accountLifecycle';

const getVisitSeatRequestsFn = httpsCallable(functions, 'getVisitSeatRequests');

export const VISIT_TYPES = {
  onsite: {
    key: 'onsite',
    requestCollection: 'onSiteRequests',
    configDoc: 'onsite',
    label: 'On-Site Visit',
    shortLabel: 'On-Site',
    accent: 'green',
  },
  cqc: {
    key: 'cqc',
    requestCollection: 'cqcVisitRequests',
    configDoc: 'cqc',
    label: 'CQC Visit',
    shortLabel: 'CQC',
    accent: 'red',
  },
};

const WINDOWS_COLLECTION = 'visitWindows';
const CONFIG_COLLECTION = 'visitConfig';

export const ACTIVE_REQUEST_STATUSES = ['requested', 'scheduled'];

/** Normalize legacy status strings to the new vocabulary. */
export function normalizeVisitStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'pending' || s === '') return 'requested';
  if (s === 'approved' || s === 'confirmed') return 'scheduled';
  if (s === 'done' || s === 'finished') return 'completed';
  if (s === 'cancelled' || s === 'canceled') return 'denied';
  return s;
}

// ---------------------------------------------------------------------------
// Date helpers (local, no timezone surprises — dates are 'YYYY-MM-DD' strings)
// ---------------------------------------------------------------------------

export function parseDateKey(key) {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(key));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

export function toDateKey(date) {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(dateKey, n) {
  const d = parseDateKey(dateKey);
  if (!d) return '';
  d.setDate(d.getDate() + n);
  return toDateKey(d);
}

export function formatWindowRange(day1, day2) {
  const d1 = parseDateKey(day1);
  const d2 = parseDateKey(day2);
  if (!d1) return '—';
  const opts = { month: 'short', day: 'numeric' };
  if (!d2) return d1.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
  if (sameMonth) {
    return `${d1.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${d2.getDate()}, ${d2.getFullYear()}`;
  }
  return `${d1.toLocaleDateString('en-US', opts)} – ${d2.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

export function todayKey() {
  return toDateKey(new Date());
}

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

export async function fetchVisitWindows(type) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) return [];
  try {
    const ref = collection(db, WINDOWS_COLLECTION);
    const snap = await getDocs(query(ref, where('type', '==', cfg.key)));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(a.day1).localeCompare(String(b.day1)));
  } catch (e) {
    console.error('fetchVisitWindows error:', e);
    return [];
  }
}

export async function createVisitWindow(type, day1, day2, capacity = 2) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) throw new Error('Unknown visit type');
  const now = new Date().toISOString();
  const payload = {
    type: cfg.key,
    day1,
    day2: day2 || addDays(day1, 1),
    capacity: Number(capacity) || 2,
    createdAt: now,
    updatedAt: now,
  };
  const ref = await addDoc(collection(db, WINDOWS_COLLECTION), payload);
  return { id: ref.id, ...payload };
}

export async function updateVisitWindow(id, data) {
  await updateDoc(doc(db, WINDOWS_COLLECTION, id), {
    ...data,
    updatedAt: new Date().toISOString(),
  });
}

export async function deleteVisitWindow(id) {
  await deleteDoc(doc(db, WINDOWS_COLLECTION, id));
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export async function fetchVisitRequests(type) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) return [];
  try {
    const ref = collection(db, cfg.requestCollection);
    let snap;
    try {
      snap = await getDocs(query(ref, orderBy('createdAt', 'desc')));
    } catch {
      snap = await getDocs(ref);
    }
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      status: normalizeVisitStatus(d.data().status),
    }));
  } catch (e) {
    console.error('fetchVisitRequests error:', e);
    return [];
  }
}

/**
 * Other companies' active bookings, as { windowId, status } only, for the portal booking
 * page's seat counts. Clients may read just their own request (firestore.rules).
 */
export async function fetchVisitSeatRequests(type) {
  if (!VISIT_TYPES[type]) return [];
  try {
    const { data } = await getVisitSeatRequestsFn({ type });
    return (data?.requests || []).map((r) => ({ ...r, status: normalizeVisitStatus(r.status) }));
  } catch (e) {
    console.error('fetchVisitSeatRequests error:', e);
    return [];
  }
}

export async function fetchUserVisitRequest(type, uid) {
  const cfg = VISIT_TYPES[type];
  if (!cfg || !uid) return null;
  const snap = await getDoc(doc(db, cfg.requestCollection, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data(), status: normalizeVisitStatus(snap.data().status) };
}

export async function submitVisitRequest(type, uid, payload) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) throw new Error('Unknown visit type');
  const now = new Date().toISOString();
  const data = {
    userId: uid,
    status: 'requested',
    createdAt: now,
    updatedAt: now,
    ...payload,
  };
  await setDoc(doc(db, cfg.requestCollection, uid), data);
  return data;
}

export async function updateVisitRequest(type, id, data) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) throw new Error('Unknown visit type');
  await updateDoc(doc(db, cfg.requestCollection, id), {
    ...data,
    updatedAt: new Date().toISOString(),
  });
}

export async function deleteVisitRequest(type, id) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) throw new Error('Unknown visit type');
  await deleteDoc(doc(db, cfg.requestCollection, id));
}

// ---------------------------------------------------------------------------
// Itinerary config (editable from CRM)
// ---------------------------------------------------------------------------

export const DEFAULT_ITINERARY = {
  cqc: [
    {
      day: 1,
      title: 'Leadership Immersion & Strategy',
      blocks: [
        { time: '8:30 AM', activity: 'Arrival, welcome & home-office tour' },
        { time: '9:00 AM', activity: 'Executive kickoff — company vision, values & culture' },
        { time: '10:30 AM', activity: 'Sit in on a live Design meeting' },
        { time: '12:00 PM', activity: 'Working lunch with the leadership team' },
        { time: '1:00 PM', activity: 'Sit in on a live Sales meeting' },
        { time: '2:30 PM', activity: 'Process & systems walkthrough' },
        { time: '4:00 PM', activity: 'Day 1 debrief and Q&A' },
      ],
    },
    {
      day: 2,
      title: 'Operations & Team Shadowing',
      blocks: [
        { time: '8:30 AM', activity: 'Morning standup with the team' },
        { time: '9:00 AM', activity: 'Department shadowing — operations & production' },
        { time: '10:30 AM', activity: 'Technology & software demonstrations' },
        { time: '12:00 PM', activity: 'Lunch & open discussion' },
        { time: '1:00 PM', activity: 'Performance management & KPIs deep dive' },
        { time: '2:30 PM', activity: 'Growth roadmap & best-practices sharing' },
        { time: '4:00 PM', activity: 'Final takeaways & action plan' },
      ],
    },
  ],
  onsite: [
    {
      day: 1,
      title: 'Business Analysis & Assessment',
      blocks: [
        { time: '8:30 AM', activity: 'Arrival & introductions' },
        { time: '9:00 AM', activity: 'Company overview & current-state analysis' },
        { time: '10:30 AM', activity: 'Financial performance review' },
        { time: '12:00 PM', activity: 'Lunch with the leadership team' },
        { time: '1:00 PM', activity: 'Marketing & lead-generation analysis' },
        { time: '2:30 PM', activity: 'Technology & operations assessment' },
        { time: '4:00 PM', activity: 'Employee interviews & feedback sessions' },
      ],
    },
    {
      day: 2,
      title: 'Strategic Planning & Implementation',
      blocks: [
        { time: '8:30 AM', activity: 'Day 2 kickoff & recap' },
        { time: '9:00 AM', activity: 'Market analysis & competitive positioning' },
        { time: '10:30 AM', activity: 'Jobsite visit & process observation' },
        { time: '12:00 PM', activity: 'Lunch & strategy discussion' },
        { time: '1:00 PM', activity: 'Growth strategy & scaling roadmap' },
        { time: '2:30 PM', activity: 'Process-improvement recommendations' },
        { time: '4:00 PM', activity: 'Action plan creation & next steps' },
      ],
    },
  ],
};

export async function fetchVisitItinerary(type) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) return DEFAULT_ITINERARY[type] || [];
  try {
    const snap = await getDoc(doc(db, CONFIG_COLLECTION, cfg.configDoc));
    if (snap.exists() && Array.isArray(snap.data().itinerary) && snap.data().itinerary.length) {
      return snap.data().itinerary;
    }
  } catch (e) {
    console.error('fetchVisitItinerary error:', e);
  }
  return DEFAULT_ITINERARY[type] || [];
}

export async function saveVisitItinerary(type, itinerary) {
  const cfg = VISIT_TYPES[type];
  if (!cfg) throw new Error('Unknown visit type');
  await setDoc(
    doc(db, CONFIG_COLLECTION, cfg.configDoc),
    { itinerary, updatedAt: new Date().toISOString() },
    { merge: true }
  );
}

// ---------------------------------------------------------------------------
// Availability for clients
// ---------------------------------------------------------------------------

/**
 * Returns future windows that still have room (active requests < capacity),
 * each annotated with how many seats remain.
 */
export function computeAvailableWindows(windows, requests, { excludeUid } = {}) {
  const today = todayKey();
  const activeByWindow = new Map();
  (requests || []).forEach((r) => {
    if (excludeUid && r.id === excludeUid) return;
    if (!r.windowId) return;
    if (!ACTIVE_REQUEST_STATUSES.includes(normalizeVisitStatus(r.status))) return;
    activeByWindow.set(r.windowId, (activeByWindow.get(r.windowId) || 0) + 1);
  });
  return (windows || [])
    .filter((w) => String(w.day1) >= today)
    .map((w) => {
      const used = activeByWindow.get(w.id) || 0;
      const capacity = Number(w.capacity) || 2;
      return { ...w, seatsUsed: used, seatsLeft: Math.max(0, capacity - used) };
    })
    .filter((w) => w.seatsLeft > 0)
    .sort((a, b) => String(a.day1).localeCompare(String(b.day1)));
}

/**
 * Internal coverage insights so the team never runs out of bookable dates.
 * Returns counts of future/open/empty windows, open seats, next open date,
 * how far out coverage runs, and health flags for low/none availability.
 */
export function computeAvailabilityInsights(windows, requests, { lowCoverageDays = 30 } = {}) {
  const today = todayKey();
  const now = parseDateKey(today);

  // Active (requested + scheduled) registrations per window.
  const activeByWindow = new Map();
  (requests || []).forEach((r) => {
    if (!r.windowId) return;
    if (!ACTIVE_REQUEST_STATUSES.includes(normalizeVisitStatus(r.status))) return;
    activeByWindow.set(r.windowId, (activeByWindow.get(r.windowId) || 0) + 1);
  });

  const future = (windows || [])
    .filter((w) => String(w.day1) >= today)
    .sort((a, b) => String(a.day1).localeCompare(String(b.day1)));

  let totalOpenSeats = 0;
  let openWindowCount = 0;
  const emptyWindows = [];
  let windowsNext = 0;
  let openSeatsNext = 0;

  const horizon = new Date(now.getTime());
  horizon.setDate(horizon.getDate() + lowCoverageDays);
  const horizonKey = toDateKey(horizon);

  future.forEach((w) => {
    const cap = Number(w.capacity) || 2;
    const used = activeByWindow.get(w.id) || 0;
    const seatsLeft = Math.max(0, cap - used);
    if (seatsLeft > 0) openWindowCount += 1;
    totalOpenSeats += seatsLeft;
    if (used === 0) emptyWindows.push({ ...w, seatsLeft });
    if (String(w.day1) <= horizonKey) {
      windowsNext += 1;
      openSeatsNext += seatsLeft;
    }
  });

  const nextOpen = future.find((w) => {
    const cap = Number(w.capacity) || 2;
    return cap - (activeByWindow.get(w.id) || 0) > 0;
  });
  const lastWindow = future.length ? future[future.length - 1] : null;

  const daysBetween = (key) => {
    const d = parseDateKey(key);
    if (!d || !now) return null;
    return Math.round((d.getTime() - now.getTime()) / 86400000);
  };

  return {
    futureWindowCount: future.length,
    openWindowCount,
    totalOpenSeats,
    emptyWindowCount: emptyWindows.length,
    emptyWindows,
    nextOpenDate: nextOpen?.day1 || null,
    daysUntilNextOpen: nextOpen ? daysBetween(nextOpen.day1) : null,
    coverageThrough: lastWindow?.day1 || null,
    daysOfCoverage: lastWindow ? daysBetween(lastWindow.day1) : null,
    windowsNextWindow: windowsNext,
    openSeatsNextWindow: openSeatsNext,
    lowCoverageDays,
    noUpcoming: future.length === 0,
    noOpen: openWindowCount === 0,
    lowCoverage: openSeatsNext === 0,
  };
}

// ---------------------------------------------------------------------------
// Outstanding visits (companies with the visit deliverable unused)
// ---------------------------------------------------------------------------

function nameMatchesVisit(name, type) {
  const n = String(name || '').toLowerCase();
  if (!n.includes('visit')) return false;
  if (type === 'cqc') return n.includes('cqc');
  return n.includes('on-site') || n.includes('onsite') || n.includes('on site');
}

/**
 * Companies whose program includes the visit deliverable but who have not yet
 * used it (no completion logged and no completed request). Heavy-ish (scans
 * active client accounts + program contract items once) — fails soft to [].
 */
export async function computeOutstandingVisits(type, requests) {
  try {
    // 1. Catalog items that represent this visit (by name).
    const itemsSnap = await getDocs(collection(db, 'alaCarteItems'));
    const visitItemIds = new Set();
    itemsSnap.docs.forEach((d) => {
      if (nameMatchesVisit(d.data().name, type)) visitItemIds.add(d.id);
    });

    // 2. Programs whose contract items include the visit (by name) + the ids used.
    const programsSnap = await getDocs(collection(db, 'programs'));
    const programIncludesVisit = new Map(); // programId -> bool
    await Promise.all(
      programsSnap.docs.map(async (pDoc) => {
        try {
          const ciSnap = await getDocs(collection(db, 'programs', pDoc.id, 'contractItems'));
          let includes = false;
          ciSnap.docs.forEach((ci) => {
            if (nameMatchesVisit(ci.data().name, type)) {
              includes = true;
              visitItemIds.add(ci.id);
            }
          });
          programIncludesVisit.set(pDoc.id, includes);
          programIncludesVisit.set(pDoc.data().name, includes);
        } catch {
          /* ignore */
        }
      })
    );

    // 3. Completed/used by uid + email from existing requests.
    const completedUids = new Set();
    const completedEmails = new Set();
    (requests || []).forEach((r) => {
      if (normalizeVisitStatus(r.status) === 'completed') {
        if (r.userId) completedUids.add(r.userId);
        if (r.userEmail) completedEmails.add(String(r.userEmail).toLowerCase());
      }
    });

    // 4. Active client accounts.
    const accountsSnap = await getDocs(
      query(collection(db, 'accounts'), where('type', '==', 'Clients'))
    );

    const outstanding = [];
    accountsSnap.docs.forEach((aDoc) => {
      const a = aDoc.data();
      if (!isActiveClientAccount(a)) return;

      // Membership: program tier includes it, OR custom/extra deliverables include a visit item.
      const programStatus =
        typeof a.program === 'string' ? a.program : a.program?.status;
      let hasDeliverable = !!programIncludesVisit.get(programStatus);
      const custom = Array.isArray(a.customProgramDeliverables) ? a.customProgramDeliverables : [];
      const extra = Array.isArray(a.extraDeliverables) ? a.extraDeliverables.map((e) => e.id) : [];
      if (!hasDeliverable && [...custom, ...extra].some((id) => visitItemIds.has(id))) {
        hasDeliverable = true;
      }
      if (!hasDeliverable) return;

      // Used? completion log on any matching deliverable id, or a completed request.
      const email = String(a.contact?.email || a.email || '').toLowerCase();
      let used = completedUids.has(aDoc.id) || (email && completedEmails.has(email));
      if (!used && a.deliverables) {
        for (const id of visitItemIds) {
          const prog = a.deliverables[id];
          if (prog && ((prog.logs && prog.logs.length) || prog.lastCompleted)) {
            used = true;
            break;
          }
        }
      }
      if (used) return;

      outstanding.push({
        accountId: aDoc.id,
        company: a.company || 'Company',
        email,
        contactName:
          a.contact?.firstName || a.contact?.lastName
            ? `${a.contact?.firstName || ''} ${a.contact?.lastName || ''}`.trim()
            : '',
      });
    });

    return outstanding.sort((a, b) =>
      a.company.localeCompare(b.company, undefined, { sensitivity: 'base' })
    );
  } catch (e) {
    console.error('computeOutstandingVisits error:', e);
    return [];
  }
}
